import { useEffect, useState, type ReactNode } from 'react';
import { allows, type CurrentUser } from '../../../api';
import { useResource } from '../../../shell/hooks';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Id,
  Money,
  Moment,
  Resourceful,
  SplitGrid,
  StatusBadge,
  type BadgeTone,
  type Column,
} from '../../../shell/kit';
import { AuditTrail } from '../../../shell/audit';
import { CustomerPicker, useSiteNames } from '../../../shell/pickers';
import { useToast } from '../../../shell/toast';
import { formatMoney } from '../../../shell/format';
import { billingStrings, type BillingKey } from '../../billing/strings';
import type { IdentityKey, IdentityTranslate } from '../i18n';
import {
  account,
  consents,
  feedback,
  offers,
  orders,
  profile,
  subscriptions,
  transactions,
  visitsPage,
  type CardFeedback,
  type CardOrder,
  type CardSubscription,
  type CardTransaction,
  type CardVehicleRow,
  type CardVisitRow,
} from './api';
import { PhoneVerifier, ProfileEditor, VehicleEditor } from './editors';
import { VisitCardPanel } from './visit';

/**
 * Карточка клиента: вся история в одном экране.
 *
 * До неё контакты жили в идентификации, бонусы в лояльности, подписка в
 * подписке, покупки в кассе, обращения в обращениях, платежи в платежах — и
 * чтобы ответить на вопрос «что это за человек», приходилось обойти шесть
 * разделов, каждый раз вводя двадцатишестисимвольный идентификатор заново.
 *
 * Секции грузятся независимо и по одной. Роль, которой секция не положена,
 * не видит её вовсе — ни данных, ни отказа: граница роли это не ошибка, и
 * красной плашки на этом месте быть не должно.
 */
export interface CardViewProps {
  readonly t: IdentityTranslate;
  readonly user: CurrentUser;
  readonly customerId: string;
  readonly onCustomerChange: (id: string) => void;
}

/** Секция карточки: своё состояние загрузки, свой отказ, свой пустой экран. */
function Section<T>({
  title,
  resource,
  children,
  actions,
}: {
  title: string;
  resource: ReturnType<typeof useResource<T>>;
  children: (data: T) => ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Card title={title} {...(actions ? { actions } : {})}>
      <Resourceful resource={resource}>{children}</Resourceful>
    </Card>
  );
}

function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line py-1.5 last:border-0">
      <span className="text-sm text-ink-muted">{label}</span>
      <span className="min-w-0 text-right text-sm text-ink">{children}</span>
    </div>
  );
}

const SUBSCRIPTION_TONE: Readonly<Record<string, BadgeTone>> = {
  active: 'ok',
  pending: 'neutral',
  past_due: 'warn',
  paused: 'warn',
  cancelled: 'neutral',
  expired: 'neutral',
};

const ORDER_TONE: Readonly<Record<string, BadgeTone>> = {
  completed: 'ok',
  confirmed: 'brand',
  in_progress: 'brand',
  draft: 'neutral',
  cancelled: 'neutral',
};

const PAYMENT_TONE: Readonly<Record<string, BadgeTone>> = {
  captured: 'ok',
  authorized: 'brand',
  pending: 'neutral',
  failed: 'danger',
  refunded: 'warn',
};

const FEEDBACK_TONE: Readonly<Record<string, BadgeTone>> = {
  new: 'brand',
  assigned: 'brand',
  answered: 'ok',
  escalated: 'danger',
  closed: 'neutral',
};

/**
 * Статус и период подписки — теми же словами, что в разделе «Подписка». Раньше
 * карточка показывала сырые `active` и `month` (QA.md, Q13). Неизвестное
 * значение выводится как есть, а не пропадает.
 */
function billingLabel(locale: string | undefined, key: string, fallback: string): string {
  const text = billingStrings(locale === 'kk' ? 'kk' : 'ru')(key as BillingKey);
  return text === key ? fallback : text;
}

export function CardView({ t, user, customerId, onCustomerChange }: CardViewProps) {
  const toast = useToast();
  const has = (id: string): boolean => id.trim().length > 0;
  const on = has(customerId);
  // Открытая карточка визита: снимки, хронология, заказы (М2.6).
  const [openVisit, setOpenVisit] = useState<string | null>(null);

  // Права считаются по матрице ядра, а не по спискам ролей: секция, которой
  // роли не положено, не запрашивается вовсе — ни одного лишнего 403 в сети.
  const canOrders = allows(user, 'order.create');
  const canPayments = allows(user, 'report.site');
  const canFeedback = allows(user, 'feedback.view');
  // История изменений — право `audit.view`; кассиру и оператору секция не
  // показывается вовсе, а не показывается пустой.
  const canAudit = allows(user, 'audit.view');
  const canEditProfile = allows(user, 'customer.create');
  const canEditVehicle = allows(user, 'vehicle.link');
  const canSeePhone = allows(user, 'customer.view_pii');
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<CardVehicleRow | null>(null);

  const profileRes = useResource(() => profile(customerId), [customerId], { enabled: on });
  const offersRes = useResource(() => offers(customerId), [customerId], { enabled: on });
  const accountRes = useResource(() => account(customerId), [customerId], { enabled: on });
  const subsRes = useResource(() => subscriptions(customerId), [customerId], { enabled: on });
  const consentsRes = useResource(() => consents(customerId), [customerId], { enabled: on });
  const ordersRes = useResource(() => orders(customerId), [customerId], {
    enabled: on && canOrders,
  });
  const paymentsRes = useResource(() => transactions(customerId), [customerId], {
    enabled: on && canPayments,
  });
  const feedbackRes = useResource(() => feedback(customerId), [customerId], {
    enabled: on && canFeedback,
  });

  const siteName = useSiteNames();

  /*
   * Визиты дочитываются страницами: первая приходит с карточкой, следующие —
   * по курсору. Раньше карточка показывала последние 20 визитов, и история
   * постоянного клиента обрывалась на прошлом месяце.
   */
  const [moreVisits, setMoreVisits] = useState<readonly CardVisitRow[]>([]);
  const [visitCursor, setVisitCursor] = useState<string | null>(null);
  const [loadingVisits, setLoadingVisits] = useState(false);
  useEffect(() => {
    setMoreVisits([]);
    setVisitCursor(profileRes.data?.visitsNextCursor ?? null);
  }, [profileRes.data]);
  const loadMoreVisits = async (): Promise<void> => {
    if (!visitCursor) return;
    setLoadingVisits(true);
    try {
      const page = await visitsPage(customerId, visitCursor);
      setMoreVisits((prev) => [...prev, ...page.items]);
      setVisitCursor(page.nextCursor);
    } catch (err) {
      toast.error(String((err as Error).message));
    } finally {
      setLoadingVisits(false);
    }
  };

  const vehicleColumns: ReadonlyArray<Column<CardVehicleRow>> = [
    {
      key: 'plate',
      header: t('identity.card.plate'),
      render: (r) => <span className="font-mono">{r.plate}</span>,
    },
    { key: 'role', header: t('identity.card.role'), render: (r) => label('role', r.role) },
    { key: 'from', header: t('identity.card.since'), render: (r) => <Moment iso={r.valid_from} /> },
    { key: 'to', header: t('identity.card.till'), render: (r) => <Moment iso={r.valid_to} /> },
    {
      key: 'car',
      header: t('identity.card.make'),
      render: (r) => (
        <span className="text-ink-muted">
          {[r.make, r.model, r.body_type ? label('body', r.body_type) : null]
            .filter(Boolean)
            .join(' · ') || '—'}
        </span>
      ),
    },
    ...(canEditVehicle
      ? [
          {
            key: 'edit',
            header: '',
            render: (r: CardVehicleRow) => (
              <Button size="sm" onClick={() => setEditingVehicle(r)}>
                {t('identity.card.edit')}
              </Button>
            ),
          },
        ]
      : []),
  ];

  /*
   * Объект — названием, способ опознания — словами. В обеих колонках стояли
   * машинные значения: хвост ULID объекта (`…6DA7G836`) и код метода
   * (`manual`). Объект знают по вывеске и коду, а не по хвосту идентификатора.
   */
  const siteLabel = (siteId: string | null): ReactNode =>
    siteName(siteId) ?? <Id value={siteId} label={t('identity.card.siteUnknown')} />;

  /**
   * Машинный код — человеческими словами.
   *
   * Незнакомый код показывается как есть: это честнее, чем прятать его за
   * прочерком, и сразу видно, что словаря не хватает.
   */
  const label = (group: string, code: string | null | undefined): string => {
    if (!code) return '—';
    const key = `identity.${group}.${code}` as IdentityKey;
    const text = t(key);
    return text === key ? code : text;
  };

  const methodLabel = (method: string): string => label('method', method);

  const visitColumns: ReadonlyArray<Column<CardVisitRow>> = [
    { key: 'at', header: t('identity.card.visitAt'), render: (r) => <Moment iso={r.entered_at} /> },
    { key: 'site', header: t('identity.card.site'), render: (r) => siteLabel(r.site_id) },
    {
      key: 'method',
      header: t('identity.card.method'),
      render: (r) => methodLabel(r.identification_method),
    },
  ];

  const orderColumns: ReadonlyArray<Column<CardOrder>> = [
    { key: 'at', header: t('identity.card.visitAt'), render: (r) => <Moment iso={r.createdAt} /> },
    { key: 'no', header: t('identity.card.orderNo'), render: (r) => `№ ${r.clientOrderNo}` },
    { key: 'site', header: t('identity.card.site'), render: (r) => siteLabel(r.siteId) },
    {
      key: 'total',
      header: t('identity.card.total'),
      numeric: true,
      render: (r) => <Money minor={r.totalMinor} />,
    },
    {
      key: 'status',
      header: t('identity.card.orderStatus'),
      render: (r) => (
        <StatusBadge tone={ORDER_TONE[r.status] ?? 'neutral'}>{label('ord', r.status)}</StatusBadge>
      ),
    },
  ];

  const paymentColumns: ReadonlyArray<Column<CardTransaction>> = [
    { key: 'at', header: t('identity.card.visitAt'), render: (r) => <Moment iso={r.createdAt} /> },
    {
      key: 'provider',
      header: t('identity.card.provider'),
      render: (r) => <span className="font-mono">{r.provider}</span>,
    },
    { key: 'kind', header: t('identity.card.kind'), render: (r) => r.kind },
    {
      key: 'amount',
      header: t('identity.card.total'),
      numeric: true,
      render: (r) => <Money minor={r.amountMinor} />,
    },
    {
      key: 'status',
      header: t('identity.card.orderStatus'),
      render: (r) => (
        <div>
          <StatusBadge tone={PAYMENT_TONE[r.status] ?? 'neutral'}>{r.status}</StatusBadge>
          {r.failureReason ? (
            <div className="mt-1 text-xs text-danger">{r.failureReason}</div>
          ) : null}
        </div>
      ),
    },
  ];

  const feedbackColumns: ReadonlyArray<Column<CardFeedback>> = [
    { key: 'at', header: t('identity.card.visitAt'), render: (r) => <Moment iso={r.createdAt} /> },
    { key: 'channel', header: t('identity.card.channel'), render: (r) => label('ch', r.channel) },
    {
      key: 'score',
      header: t('identity.card.score'),
      numeric: true,
      render: (r) => r.score ?? '—',
    },
    {
      key: 'body',
      header: t('identity.card.text'),
      render: (r) => <span className="text-ink-muted">{r.body ?? '—'}</span>,
    },
    {
      key: 'status',
      header: t('identity.card.orderStatus'),
      render: (r) => (
        <div>
          <StatusBadge tone={FEEDBACK_TONE[r.status] ?? 'neutral'}>
            {label('fb', r.status)}
          </StatusBadge>
          {r.overdue ? (
            <div className="mt-1 text-xs text-danger">{t('identity.card.overdue')}</div>
          ) : null}
        </div>
      ),
    },
  ];

  /*
   * Открытую карточку видно и по ссылке, и после поиска. Сам `CustomerPicker`
   * помнит только тот выбор, который сделали руками: придя по присланной ссылке,
   * он показывал пустое поле поиска, и было не понять, чья это карточка вообще.
   * Поэтому когда клиент уже загружен, вместо поиска стоит его имя.
   */
  const opened = profileRes.data?.customer;
  const picker = (
    <Card>
      <div className="flex flex-wrap items-end justify-between gap-3">
        {on && opened ? (
          <div className="min-w-0">
            <p className="text-xs font-medium tracking-wider text-ink-muted uppercase">
              {t('identity.card.pick')}
            </p>
            <p className="mt-0.5 text-lg font-semibold text-ink">
              {opened.displayName ?? t('identity.card.noName')}
            </p>
            <p className="text-sm text-ink-muted">{opened.phone ?? '—'}</p>
          </div>
        ) : (
          <Field
            label={t('identity.card.pick')}
            hint={t('identity.card.pickHint')}
            className="min-w-72 flex-1"
          >
            <CustomerPicker value={customerId} onChange={(id) => onCustomerChange(id)} />
          </Field>
        )}

        {on ? (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex h-8 items-center justify-center rounded-control border border-line-strong px-3 text-sm font-medium text-ink transition hover:bg-sunken"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(window.location.href)
                  .then(() => toast.ok(t('identity.card.linkCopied')));
              }}
            >
              {t('identity.card.copyLink')}
            </button>
            <button
              type="button"
              className="inline-flex h-8 items-center justify-center rounded-control px-3 text-sm font-medium text-brand-700 transition hover:bg-brand-50"
              onClick={() => onCustomerChange('')}
            >
              {t('identity.card.another')}
            </button>
          </div>
        ) : null}
      </div>
    </Card>
  );

  if (!on) {
    return (
      <div className="flex flex-col gap-4">
        {picker}
        <EmptyState title={t('identity.card.none')} reason={t('identity.card.noneReason')} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {picker}

      <SplitGrid>
        <div className="flex flex-col gap-4">
          <Section
            title={t('identity.card.profile')}
            resource={profileRes}
            {...(canEditProfile && opened && opened.status !== 'forgotten'
              ? {
                  actions: (
                    <Button size="sm" onClick={() => setEditingProfile(true)}>
                      {t('identity.card.edit')}
                    </Button>
                  ),
                }
              : {})}
          >
            {(data) => (
              <div className="flex flex-col">
                <Line label={t('identity.card.pick')}>
                  {data.customer.displayName ?? t('identity.card.noName')}
                </Line>
                <Line label={t('identity.card.type')}>
                  {label('type', data.profile?.type ?? 'individual')}
                  {data.profile?.bin ? ` · ${t('identity.card.bin')} ${data.profile.bin}` : ''}
                </Line>
                {data.profile?.companyName ? (
                  <Line label={t('identity.card.company')}>{data.profile.companyName}</Line>
                ) : null}
                <Line label={t('identity.card.phone')}>
                  <span className="inline-flex flex-wrap items-center justify-end gap-2">
                    <span>{data.customer.phone ?? '—'}</span>
                    {data.profile?.phoneVerifiedAt ? (
                      <StatusBadge tone="ok">{t('identity.card.phoneVerified')}</StatusBadge>
                    ) : data.customer.phone && canSeePhone ? (
                      <PhoneVerifier t={t} phone={data.customer.phone} onVerified={profileRes.reload} />
                    ) : data.customer.phone ? (
                      <span className="text-xs text-ink-muted">{t('identity.card.phoneNotVerified')}</span>
                    ) : null}
                  </span>
                </Line>
                <Line label={t('identity.card.email')}>
                  {data.profile?.email ?? t('identity.card.email.none')}
                </Line>
                <Line label={t('identity.card.firstVisit')}>
                  <Moment iso={data.profile?.firstVisitAt ?? null} />
                </Line>
                <Line label={t('identity.card.lastVisit')}>
                  <Moment iso={data.profile?.lastVisitAt ?? null} />
                </Line>
                <Line label={t('identity.card.churn')}>
                  {data.profile?.churnScore === null || data.profile?.churnScore === undefined ? (
                    <span className="text-ink-muted">{t('identity.card.churnNone')}</span>
                  ) : (
                    <span title={t('identity.card.churnHint')}>
                      <StatusBadge
                        tone={
                          data.profile.churnScore >= 0.7
                            ? 'danger'
                            : data.profile.churnScore >= 0.4
                              ? 'warn'
                              : 'ok'
                        }
                      >
                        {Math.round(data.profile.churnScore * 100)} %
                      </StatusBadge>
                    </span>
                  )}
                </Line>
                <Line label={t('identity.card.status')}>
                  <StatusBadge tone={data.customer.status === 'active' ? 'ok' : 'neutral'}>
                    {label('st', data.customer.status)}
                  </StatusBadge>
                </Line>
                <Line label="ID">
                  <Id value={data.customer.id} />
                </Line>
              </div>
            )}
          </Section>

          <Section title={t('identity.card.loyalty')} resource={offersRes}>
            {(bundle) => (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col">
                  <Line label={t('identity.card.tier')}>
                    {bundle.currentTier ? (
                      <StatusBadge tone="brand">{bundle.currentTier.name}</StatusBadge>
                    ) : (
                      <span className="text-ink-muted">{t('identity.card.noTier')}</span>
                    )}
                  </Line>
                  {bundle.nextTier ? (
                    <Line label={t('identity.card.nextTier', { name: bundle.nextTier.name })}>
                      {[
                        bundle.nextTier.missingVisits
                          ? t('identity.card.needVisits', { n: bundle.nextTier.missingVisits })
                          : null,
                        bundle.nextTier.missingSpendMinor
                          ? t('identity.card.needSpend', {
                              sum: formatMoney(bundle.nextTier.missingSpendMinor),
                            })
                          : null,
                        bundle.nextTier.missingAccruedMinor
                          ? t('identity.card.needAccrued', {
                              sum: formatMoney(bundle.nextTier.missingAccruedMinor),
                            })
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </Line>
                  ) : null}
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-ink-muted uppercase">
                    {t('identity.card.offers')}
                  </p>
                  {bundle.offers.length === 0 ? (
                    <p className="text-sm text-ink-muted">{t('identity.card.noOffers')}</p>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {bundle.offers.map((offer) => (
                        <li key={offer.ruleId} className="text-sm text-ink">
                          {offer.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </Section>

          <Section title={t('identity.card.balance')} resource={accountRes}>
            {(acc) =>
              acc ? (
                <div className="flex flex-col">
                  <Line label={t('identity.card.balance')}>
                    <Money minor={acc.balanceMinor} />
                  </Line>
                  <Line label={t('identity.card.materialized')}>
                    <Money minor={acc.materializedMinor} />
                  </Line>
                  <Line label={t('identity.card.drift')}>
                    <span className={acc.driftMinor === 0 ? '' : 'text-danger'}>
                      <Money minor={acc.driftMinor} />
                    </span>
                  </Line>
                </div>
              ) : (
                <p className="text-sm text-ink-muted">{t('identity.card.noAccount')}</p>
              )
            }
          </Section>

          <Section title={t('identity.card.subscriptions')} resource={subsRes}>
            {(rows) =>
              rows.length === 0 ? (
                <EmptyState
                  title={t('identity.card.noSubscriptions')}
                  reason={t('identity.card.noSubscriptionsReason')}
                />
              ) : (
                <div className="flex flex-col gap-4">
                  {rows.map((sub: CardSubscription) => (
                    <div key={sub.id} className="flex flex-col">
                      <Line label={t('identity.card.plan')}>
                        <span className="font-mono">{sub.planCode}</span>
                      </Line>
                      <Line label={t('identity.card.status')}>
                        <StatusBadge tone={SUBSCRIPTION_TONE[sub.status] ?? 'neutral'}>
                          {billingLabel(user.locale, `billing.status.${sub.status}`, sub.status)}
                        </StatusBadge>
                      </Line>
                      <Line label={t('identity.card.period')}>
                        {billingLabel(user.locale, `billing.period.${sub.planPeriod}`, sub.planPeriod)}
                      </Line>
                      <Line label={t('identity.card.until')}>
                        <Moment iso={sub.currentPeriodEnd} />
                      </Line>
                      <Line label={t('identity.card.autorenew')}>
                        {sub.autorenew
                          ? t('identity.card.autorenewOn')
                          : t('identity.card.autorenewOff')}
                      </Line>
                    </div>
                  ))}
                </div>
              )
            }
          </Section>

          <Section title={t('identity.card.vehicles')} resource={profileRes}>
            {(data) => (
              <DataTable
                rows={data.vehicles}
                columns={vehicleColumns}
                rowKey={(r) => r.id}
                empty={t('identity.card.noVehicles')}
                emptyReason={t('identity.card.noVehiclesReason')}
              />
            )}
          </Section>

          <Section title={t('identity.card.consents')} resource={consentsRes}>
            {(data) => (
              <div className="flex flex-col">
                {(
                  [
                    ['data_processing', 'identity.card.consentData'],
                    ['marketing', 'identity.card.consentMarketing'],
                  ] as const
                ).map(([kind, label]) => (
                  <Line key={kind} label={t(label as IdentityKey)}>
                    <StatusBadge tone={data.current[kind]?.active ? 'ok' : 'warn'}>
                      {data.current[kind]?.active
                        ? t('identity.card.granted')
                        : t('identity.card.revoked')}
                    </StatusBadge>
                  </Line>
                ))}
              </div>
            )}
          </Section>
        </div>

        <div className="flex flex-col gap-4">
          <Section title={t('identity.card.visits')} resource={profileRes}>
            {(data) => (
              <div className="flex flex-col gap-3">
                <DataTable
                  rows={[...data.visits, ...moreVisits]}
                  columns={visitColumns}
                  rowKey={(r) => r.id}
                  empty={t('identity.card.noVisits')}
                  onRowClick={(r) => setOpenVisit(r.id)}
                  {...(openVisit ? { activeKey: openVisit } : {})}
                />
                {visitCursor ? (
                  <Button disabled={loadingVisits} onClick={() => void loadMoreVisits()}>
                    {t('identity.card.moreVisits')}
                  </Button>
                ) : data.visits.length > 0 ? (
                  <p className="text-xs text-ink-muted">{t('identity.card.allVisits')}</p>
                ) : null}
              </div>
            )}
          </Section>

          {openVisit ? (
            <VisitCardPanel t={t} visitId={openVisit} onClose={() => setOpenVisit(null)} />
          ) : null}

          {canOrders ? (
            <Section title={t('identity.card.orders')} resource={ordersRes}>
              {(rows) => (
                <DataTable
                  rows={rows}
                  columns={orderColumns}
                  rowKey={(r) => r.id}
                  empty={t('identity.card.noOrders')}
                />
              )}
            </Section>
          ) : null}

          {canPayments ? (
            <Section title={t('identity.card.payments')} resource={paymentsRes}>
              {(rows) => (
                <DataTable
                  rows={rows}
                  columns={paymentColumns}
                  rowKey={(r) => r.id}
                  empty={t('identity.card.noPayments')}
                />
              )}
            </Section>
          ) : null}

          {canFeedback ? (
            <Section title={t('identity.card.feedback')} resource={feedbackRes}>
              {(rows) => (
                <DataTable
                  rows={rows}
                  columns={feedbackColumns}
                  rowKey={(r) => r.id}
                  empty={t('identity.card.noFeedback')}
                />
              )}
            </Section>
          ) : null}

          {/*
            * M1-13: «по любой записи можно получить последовательность
            * изменений — кто, когда, что». Журнал писался с самого начала,
            * читать его на карточке было нечем.
            */}
          {canAudit ? <AuditTrail entityId={customerId} title={t('identity.card.history')} /> : null}
        </div>
      </SplitGrid>

      {editingProfile && profileRes.data ? (
        <ProfileEditor
          t={t}
          customerId={customerId}
          displayName={profileRes.data.customer.displayName}
          email={profileRes.data.profile?.email ?? null}
          canEditEmail={canSeePhone}
          onClose={() => setEditingProfile(false)}
          onSaved={profileRes.reload}
        />
      ) : null}
      {editingVehicle ? (
        <VehicleEditor
          t={t}
          vehicle={editingVehicle}
          onClose={() => setEditingVehicle(null)}
          onSaved={profileRes.reload}
        />
      ) : null}
    </div>
  );
}
